/**
 * 55 — the spinning record.
 *
 * A CSS conic-gradient disc with repeating radial grooves, one `rotate`
 * keyframe whose `animation-play-state` is bound to `audio.paused`, and an SVG
 * tonearm rotated by lerp(start, end, currentTime / duration).
 *
 * That's it. It's about forty lines of CSS and it runs at 60fps on hardware
 * that struggles to open a PDF, because every single thing it does is a
 * compositor transform. No canvas. No WebGL. No requestAnimationFrame driving
 * a paint. The browser spins the disc on the GPU and we never think about it
 * again.
 *
 * Structure — and the nesting is load-bearing:
 *
 *   .vinyl                 the box, and the drag surface
 *     .vinyl-spin          our rotation (drag-to-scrub), set inline
 *       .vinyl-disc        the spin keyframe
 *     svg.vinyl-arm        the tonearm
 *     .vinyl-scrub         the readout while a finger is down
 *
 * Two nested rotating elements rather than one, because a CSS animation
 * outranks an inline style in the cascade — you cannot drag a disc that a
 * keyframe is also transforming. Composing them means the drag adds to the
 * spin instead of fighting it, and when you let go, the record carries on from
 * exactly where your finger left it instead of snapping back to zero.
 */

/* -------------------------------------------------------------- the tonearm */
/*
 * Geometry, worked out once so nobody has to squint at it again.
 *
 * viewBox is 0..100. The disc is centred at (50,50) with radius 50. The arm
 * pivots at (86,14) — back right, where a real one lives — and at zero
 * rotation its needle would sit at (50,52), i.e. dead centre.
 *
 * Rotating the arm about its pivot swings the needle out across the record:
 *
 *   -52deg  needle lands at r≈46 — the outer rim, where a record starts
 *   -18deg  needle lands at r≈17 — the edge of the label, where it ends
 *
 * So the arm sweeps ARM_START → ARM_END as the song plays, which is exactly
 * the motion your eye already knows.
 */
const ARM_START = -52;
const ARM_END = -18;

/** One full turn of the record scrubs this many seconds. */
const SCRUB_SECONDS_PER_TURN = 30;

/** Below this, a tonearm is four grey pixels. Don't draw one. */
const ARM_MIN_PX = 72;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export interface VinylOptions {
  /** the label colour — the song's colour */
  color?: string;
  /** draw the tonearm (default: true) */
  arm?: boolean;
  /**
   * Enable drag-to-scrub. Called continuously while dragging, then once more
   * on release. Omit it and the disc is decorative — which is what you want on
   * a 48px vinyl in the now-playing bar.
   */
  onScrub?: (seconds: number) => void;
  /** Where are we now? Needed to know what a drag is relative to. */
  getTime?: () => { time: number; duration: number };
}

export interface Vinyl {
  el: HTMLElement;
  /** Bound to !audio.paused. The browser does the rest. */
  setPlaying(playing: boolean): void;
  setProgress(time: number, duration: number): void;
  setColor(color: string): void;
  destroy(): void;
}

const fmt = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

export function createVinyl(opts: VinylOptions = {}): Vinyl {
  const { color = '#ff3333', arm = true, onScrub, getTime } = opts;

  const el = document.createElement('div');
  el.className = 'vinyl';
  el.dataset.playing = '0';
  el.style.setProperty('--disc-color', color);

  const spin = document.createElement('div');
  spin.className = 'vinyl-spin';

  const disc = document.createElement('div');
  disc.className = 'vinyl-disc';

  spin.appendChild(disc);
  el.appendChild(spin);

  let armGroup: SVGGElement | null = null;
  if (arm) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'vinyl-arm');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = `
      <g class="arm-group">
        <!-- counterweight, behind the pivot -->
        <circle cx="96" cy="4" r="4.5" fill="#2a2a2a" stroke="rgba(255,255,255,.14)" stroke-width="0.8"/>
        <!-- the tube -->
        <line x1="86" y1="14" x2="52" y2="50" stroke="#c9c9c9" stroke-width="2.4" stroke-linecap="round"/>
        <line x1="86" y1="14" x2="52" y2="50" stroke="rgba(255,255,255,.55)" stroke-width="0.8" stroke-linecap="round"/>
        <!-- headshell -->
        <rect x="46" y="45" width="10" height="8" rx="2"
              transform="rotate(-45 51 49)" fill="#e6e6e6"/>
        <!-- the needle -->
        <circle cx="50" cy="52" r="1.6" fill="#ff3333"/>
        <!-- pivot -->
        <circle cx="86" cy="14" r="6.5" fill="#1e1e1e" stroke="rgba(255,255,255,.18)" stroke-width="1"/>
        <circle cx="86" cy="14" r="2.4" fill="#8a8a8a"/>
      </g>`;
    el.appendChild(svg);
    armGroup = svg.querySelector('g');
  }

  let scrubOut: HTMLElement | null = null;
  if (onScrub) {
    scrubOut = document.createElement('div');
    scrubOut.className = 'vinyl-scrub';
    el.appendChild(scrubOut);
  }

  /* ------------------------------------------------------------- the arm */

  // The arm sweeps ~34 degrees across a whole song. At 60fps that's about two
  // thousandths of a degree per frame — writing it every frame would be pure
  // waste. Write it when it has actually moved.
  let lastArmAngle = NaN;

  function setArm(t: number, d: number) {
    if (!armGroup) return;
    const p = d > 0 ? Math.min(1, Math.max(0, t / d)) : 0;
    const a = lerp(ARM_START, ARM_END, p);
    if (Number.isNaN(lastArmAngle) || Math.abs(a - lastArmAngle) >= 0.05) {
      lastArmAngle = a;
      // The SVG transform attribute, not CSS. transform-box/transform-origin on
      // SVG has a long history of Safari bugs and this thing has to work on a
      // phone that is five years old.
      armGroup.setAttribute('transform', `rotate(${a.toFixed(2)} 86 14)`);
    }
  }

  setArm(0, 0);

  /* ------------------------------------------------------ drag to scrub */

  let dragging = false;
  let dragPointer = -1;
  let lastAngle = 0;
  /** total signed rotation of this drag, in degrees; can exceed 360 */
  let sweep = 0;
  /** the record's resting rotation, so it never snaps back on release */
  let baseRotation = 0;
  let dragStartTime = 0;
  let dragDuration = 0;
  let pendingTime = 0;
  let scrubRaf = 0;

  const angleAt = (e: PointerEvent): number => {
    const r = el.getBoundingClientRect();
    return (
      (Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180) /
      Math.PI
    );
  };

  function onDown(e: PointerEvent) {
    if (!onScrub || !getTime) return;
    const { time, duration } = getTime();
    if (!(duration > 0)) return; // nothing to scrub through yet

    dragging = true;
    dragPointer = e.pointerId;
    lastAngle = angleAt(e);
    sweep = 0;
    dragStartTime = time;
    dragDuration = duration;
    pendingTime = time;

    el.dataset.dragging = '1';

    // Best effort, and it MUST NOT throw past this point. If the pointer is
    // already gone by the time we get here, setPointerCapture raises
    // NotFoundError — and if that escaped, we'd have already set `dragging` and
    // `data-dragging`, so the record would stay frozen with its spin paused,
    // forever, and the app would look broken. Capture is an optimisation (it
    // keeps the moves coming when a finger slides off the disc); the drag works
    // without it, and the window-level listeners below guarantee it ends.
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* no capture — pointermove on the element still works */
    }
    e.preventDefault();
  }

  function onMove(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointer) return;

    const a = angleAt(e);
    let delta = a - lastAngle;
    // atan2 wraps at ±180; unwrap so a slow drag past the top doesn't read as
    // a violent 359-degree yank backwards.
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    lastAngle = a;
    sweep += delta;

    // The record turns under your finger, exactly as far as your finger went.
    spin.style.transform = `rotate(${baseRotation + sweep}deg)`;

    // One full turn = 30 seconds. A real record at 33rpm would scrub 1.8s per
    // turn, which would mean 166 turns to cross a five-minute song. This is a
    // deliberate lie, and it's the one that makes the gesture usable.
    const t = Math.min(
      dragDuration,
      Math.max(0, dragStartTime + (sweep / 360) * SCRUB_SECONDS_PER_TURN),
    );
    pendingTime = t;

    if (scrubOut) scrubOut.textContent = fmt(t);
    setArm(t, dragDuration);

    // Seek at most once a frame. Hammering currentTime on every pointermove
    // makes a cheap Android stutter and the audio gargle.
    if (!scrubRaf) {
      scrubRaf = requestAnimationFrame(() => {
        scrubRaf = 0;
        onScrub?.(pendingTime);
      });
    }
    e.preventDefault();
  }

  function onUp(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointer) return;
    dragging = false;
    dragPointer = -1;

    // Keep the rotation we ended on. The record doesn't rewind itself when you
    // take your finger off, and neither does this.
    baseRotation = (baseRotation + sweep) % 360;
    spin.style.transform = `rotate(${baseRotation}deg)`;

    delete el.dataset.dragging;
    if (scrubRaf) {
      cancelAnimationFrame(scrubRaf);
      scrubRaf = 0;
    }
    onScrub?.(pendingTime);
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* already gone */
    }
  }

  if (onScrub && getTime) {
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    // The safety net. If pointer capture didn't take, a finger that lifts
    // outside the disc would never fire pointerup *on the disc* — and the
    // record would sit there, frozen, with the spin paused. The window always
    // hears the release.
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /* ------------------------------------------------------------- resize */

  // Hide the arm on a vinyl too small to show one.
  const ro =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
          const w = entries[0]?.contentRect.width ?? 0;
          const svg = el.querySelector<SVGElement>('.vinyl-arm');
          if (svg) svg.style.display = w < ARM_MIN_PX ? 'none' : '';
        })
      : null;
  ro?.observe(el);

  return {
    el,

    setPlaying(playing: boolean) {
      // This single attribute is the whole play/pause animation. The CSS binds
      // animation-play-state to it; the compositor does the rest, on its own
      // thread, whether or not our JS is busy.
      el.dataset.playing = playing ? '1' : '0';
    },

    setProgress(time: number, duration: number) {
      if (dragging) return; // the finger wins
      setArm(time, duration);
    },

    setColor(c: string) {
      el.style.setProperty('--disc-color', c);
    },

    destroy() {
      ro?.disconnect();
      if (scrubRaf) cancelAnimationFrame(scrubRaf);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      el.remove();
    },
  };
}
